/**
 * @file PeriodBar.tsx
 * @brief Period picker (7 / 30 / 90 days, a month, custom) and the GBP / USD
 *        switch for Super Admin → Analytics (W22).
 *
 * @details
 * Hairline GridCells row like the old AnalyticsRangeBar. Controlled: the page
 * owns the period and the currency. The window itself comes from
 * `windowFor` (businessMath), so what is sent is what is labelled.
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { useMemo } from "react";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select";
import { GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import { flatAnalyticsFilterControlClass } from "@/features/analytics/components/AnalyticsRangeBar";
import { cn } from "@/shared/theme/cn";
import { monthKey, recentMonths, type Period, type PeriodKind } from "../businessMath";
import type { Currency } from "../types";

const PRESETS: { id: PeriodKind; label: string }[] = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "month", label: "Month" },
  { id: "custom", label: "Custom" },
];

function monthName(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

const segment = (active: boolean) =>
  cn(
    "flex-1 whitespace-nowrap rounded-none border-0 px-2 text-sm font-medium transition-colors",
    active
      ? "bg-brand-soft text-brand"
      : "bg-transparent text-muted-foreground hover:bg-secondary/40 hover:text-foreground",
  );

export function PeriodBar({
  period,
  onPeriod,
  currency,
  onCurrency,
}: {
  period: Period;
  onPeriod: (next: Period) => void;
  currency: Currency;
  onCurrency: (next: Currency) => void;
}) {
  const months = useMemo(() => recentMonths(new Date(), 12), []);

  return (
    <GridCells className="grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,9rem)]">
      <GridCell padded={false}>
        <div className="flex h-11 overflow-x-auto" role="group" aria-label="Period">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={period.kind === p.id}
              onClick={() =>
                onPeriod(
                  p.id === "month"
                    ? { kind: "month", month: period.month ?? monthKey(new Date()) }
                    : p.id === "custom"
                      ? { ...period, kind: "custom" }
                      : { kind: p.id },
                )
              }
              className={segment(period.kind === p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </GridCell>

      <GridCell padded={false}>
        {period.kind === "month" ? (
          <Select value={period.month ?? months[0]} onValueChange={(m) => onPeriod({ kind: "month", month: m })}>
            <SelectTrigger className={flatAnalyticsFilterControlClass} aria-label="Month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m} value={m}>
                  {monthName(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <DateRangePicker
            from={period.kind === "custom" ? period.customFrom : undefined}
            to={period.kind === "custom" ? period.customTo : undefined}
            onChange={(next) => onPeriod({ kind: "custom", customFrom: next.from, customTo: next.to })}
            size="sm"
            placeholder="Custom range"
            className={flatAnalyticsFilterControlClass}
          />
        )}
      </GridCell>

      <GridCell padded={false}>
        <div className="flex h-11" role="group" aria-label="Currency">
          {(["gbp", "usd"] as const).map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={currency === c}
              onClick={() => onCurrency(c)}
              className={segment(currency === c)}
            >
              {c === "gbp" ? "£ GBP" : "$ USD"}
            </button>
          ))}
        </div>
      </GridCell>
    </GridCells>
  );
}
